import { Capacitor, registerPlugin } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Browser } from '@capacitor/browser';

// Define the custom plugin
const NativeDownloadManager = registerPlugin('NativeDownloadManager');

/**
 * Downloads a file from a URL using native capabilities
 * - Android: Uses custom DownloadManager plugin (handles notifications natively)
 * - iOS: Saves to Documents and opens with system browser
 * - Web: Opens in new tab
 * @param {string} url - The URL of the file to download
 * @param {string} filename - The desired filename
 */
export const downloadFile = async (url, filename) => {
    if (!Capacitor.isNativePlatform()) {
        // Fallback for web
        window.open(url, '_blank');
        return;
    }

    try {
        const fileName = filename || url.substring(url.lastIndexOf('/') + 1) || 'downloaded_file';

        if (Capacitor.getPlatform() === 'android') {
            // Get i18n instance for localized strings
            const i18n = (await import('../i18n')).default;
            const t = i18n.t.bind(i18n);

            // Use native Android DownloadManager plugin with localized notifications
            // This handles everything: download, notifications, and file opening
            await NativeDownloadManager.download({
                url: url,
                filename: fileName,
                notificationTitle: fileName,
                notificationDescription: t('downloading_file', { defaultValue: 'Downloading file...' })
            });
            return;
        }

        // iOS Implementation: Save to Documents and open in browser
        // 1. Fetch the file content
        const response = await fetch(url);
        const blob = await response.blob();

        // 2. Convert blob to base64
        const reader = new FileReader();
        const base64Data = await new Promise((resolve, reject) => {
            reader.onload = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });

        // 3. Save to Documents
        const result = await Filesystem.writeFile({
            path: fileName,
            data: base64Data,
            directory: Directory.Documents,
            recursive: true
        });

        // 4. Show success message (localized)
        const i18n = (await import('../i18n')).default;
        const t = i18n.t.bind(i18n);
        const antdMessage = (await import('antd')).message;
        antdMessage.success(t('download_complete', { defaultValue: 'Download Complete' }));

        // 5. Open the file in the browser (iOS will show "Open in..." options)
        await Browser.open({ url });

    } catch (error) {
        console.error('Download error:', error);
        // Fallback to browser open if native fails
        await Browser.open({ url });
    }
};

